import i18n from "@/utils/i18n";
import { apiClient } from "@/lib/apiClient";
import type { ApprovalTargetSummary } from "@/features/approval-flows/types/approval-flows.types";
import type {
  EducationListItemApi,
  EducationListResponseApi,
  EducationListParams,
} from "@/features/education-management/types/education-management.types";
import {
  EDUCATION_TYPE_API,
  EDUCATION_STATUS_API,
} from "@/features/education-management/types/education-management.types";
import type {
  RequestAndApprovalEntry,
  RequestAndApprovalFilters,
  RequestStatus,
  EducationFormat as RequestApprovalFormat,
  PersonnelRequestEntry,
  DepartmentWithPersonnel,
  ApproveWithClassGroupsPayload,
  ApproveWithClassGroupsResult,
  PlanningGroupsResponseApi,
  PlanningGroupFromApi,
} from "../types/request-and-approval.types";

const ADMIN_EDUCATIONS_ENDPOINT = "/api/admin/educations";

function pickTrimmedString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

/** planning-groups yanıtında sanal sınıf id’si farklı property adlarıyla gelebilir */
function normalizePlanningGroupFromApi(raw: unknown): PlanningGroupFromApi {
  const o = raw as Record<string, unknown>;
  const base = raw as PlanningGroupFromApi;
  const virtualClassroomId =
    pickTrimmedString(o.id) ??
    pickTrimmedString(o.virtualClassroomId) ??
    pickTrimmedString(o.virtualClassroomID) ??
    pickTrimmedString(o.virtual_classroom_id) ??
    pickTrimmedString(o.classroomId) ??
    pickTrimmedString(o.classroomID) ??
    pickTrimmedString(o.classroom_id);

  return {
    ...base,
    id: virtualClassroomId ?? base.id,
    virtualClassroomId: pickTrimmedString(o.virtualClassroomId) ?? base.virtualClassroomId,
    virtualClassroomID: pickTrimmedString(o.virtualClassroomID) ?? base.virtualClassroomID,
  };
}

/** GET /api/admin/educations/:educationId/enrollments - backend kayıt tipi */
export interface EducationEnrollmentUserApi {
  id: string;
  firstName?: string;
  lastName?: string;
  identityNumber?: string;
  title?: string;
  department?: { id: string; name: string };
}

export interface EducationEnrollmentItemApi {
  id: string;
  educationID: string;
  userID: string;
  user: EducationEnrollmentUserApi;
  status: number;
  requestedAt: string;
  reviewedAt?: string | null;
  reviewerUserID?: string | null;
  progressPercent?: number;
  lastActivityAt?: string | null;
  completedAt?: string | null;
  attendances?: Array<{ attendanceDate: string; status: number }>;
  createdAt: string;
  updatedAt: string;
  isBanned?: boolean;
  approval?: ApprovalTargetSummary | null;
}

/** GET /api/admin/educations/:educationId/enrollments - response */
export interface EducationEnrollmentsResponseApi {
  success: boolean;
  status: number;
  data: EducationEnrollmentItemApi[];
  count?: number;
  total?: number;
}

/** Tarih string'ini DD.MM.YYYY formatına çevirir */
function formatDisplayDate(isoOrDate: string | null | undefined): string {
  if (!isoOrDate) return "";
  try {
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return isoOrDate;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return isoOrDate;
  }
}

/** Backend type (0-3) -> Request-and-approval format */
function apiTypeToRequestApprovalFormat(type: number): RequestApprovalFormat {
  switch (type) {
    case EDUCATION_TYPE_API.ONLINE:
      return "Canlı";
    case EDUCATION_TYPE_API.UNIT:
      return "Üniteli";
    case EDUCATION_TYPE_API.NONUNIT:
      return "Ünitesiz";
    case EDUCATION_TYPE_API.ON_SITE:
    default:
      return "Yüz Yüze";
  }
}

/** Backend status (0-4) -> Request-and-approval status */
function apiStatusToRequestApprovalStatus(status: number): RequestStatus {
  switch (status) {
    case EDUCATION_STATUS_API.REQUEST_COLLECTION:
      return "manage_requests";
    case EDUCATION_STATUS_API.BUDGET_PENDING:
      return "sent_for_approval";
    case EDUCATION_STATUS_API.ACTIVE:
    case EDUCATION_STATUS_API.CLOSED:
    case EDUCATION_STATUS_API.ARCHIVED:
    default:
      return "approved";
  }
}

/** UI format -> backend type */
function requestApprovalFormatToApiType(
  format: RequestApprovalFormat,
): number | undefined {
  if (format === "Canlı") return EDUCATION_TYPE_API.ONLINE;
  if (format === "Üniteli") return EDUCATION_TYPE_API.UNIT;
  if (format === "Ünitesiz") return EDUCATION_TYPE_API.NONUNIT;
  if (format === "Yüz Yüze") return EDUCATION_TYPE_API.ON_SITE;
  return undefined;
}

/** UI status -> backend status(ler) */
function requestApprovalStatusToApiStatuses(status: RequestStatus): number[] {
  switch (status) {
    case "manage_requests":
      return [EDUCATION_STATUS_API.REQUEST_COLLECTION];
    case "sent_for_approval":
      return [EDUCATION_STATUS_API.BUDGET_PENDING];
    case "approved":
      return [
        EDUCATION_STATUS_API.ACTIVE,
        EDUCATION_STATUS_API.CLOSED,
        EDUCATION_STATUS_API.ARCHIVED,
      ];
    default:
      return [];
  }
}

function mapApiItemToRequestAndApprovalEntry(
  item: EducationListItemApi,
): RequestAndApprovalEntry {
  const startDate =
    item.requestCollectionStartDate ?? item.startDate ?? item.createdAt ?? "";
  const endDate =
    item.requestCollectionEndDate ??
    item.endDate ??
    item.startDate ??
    item.createdAt ??
    "";
  return {
    id: item.id,
    educationId: item.id,
    type: item.type,
    educationName: item.name ?? "",
    format: apiTypeToRequestApprovalFormat(item.type),
    requestCount: `${item.totalEnrolledCount ?? 0} / ${item.capacity ?? 0}`,
    requestStartDate: formatDisplayDate(startDate),
    requestEndDate: formatDisplayDate(endDate),
    status: apiStatusToRequestApprovalStatus(item.status),
    isCurrentUserManager: item.isCurrentUserManager === true,
  };
}

/**
 * Backend enrollment status (GET enrollments response).
 * - 0 - REQUESTED (Requested)
 * - 1 - APPROVED_BRANCH_MANAGER (Approved by branch manager)
 * - 2 - ACTIVE (Approved by central manager)
 * - 3 - REJECTED_BY_BRANCH_MANAGER (Rejected by branch manager)
 * - 4 - REJECTED_BY_CENTRAL_MANAGER (Rejected by central manager)
 * - 5 - CANCELLED (Cancelled)
 * - 6 - COMPLETED (Completed)
 */
const ENROLLMENT_STATUS = {
  REQUESTED: 0,
  APPROVED_EDUCATION_SUPERVISOR: 15,
  APPROVED_BRANCH_MANAGER: 1,
  ACTIVE: 2,
  REJECTED_BY_BRANCH_MANAGER: 3,
  REJECTED_BY_CENTRAL_MANAGER: 4,
  CANCELLED: 5,
  COMPLETED: 6,
} as const;

/** Backend enrollment status -> officer ekranı durumu */
function apiEnrollmentStatusToPersonnelStatus(
  status: number,
): PersonnelRequestEntry["status"] {
  switch (status) {
    case ENROLLMENT_STATUS.APPROVED_BRANCH_MANAGER:
      return "ucuncu_onay_bekliyor";
    case ENROLLMENT_STATUS.APPROVED_EDUCATION_SUPERVISOR:
      return "ikinci_onay_bekliyor";
    case ENROLLMENT_STATUS.ACTIVE:
      return "onaylandi";
    case ENROLLMENT_STATUS.COMPLETED:
      return "tamamlandi";
    case ENROLLMENT_STATUS.REJECTED_BY_BRANCH_MANAGER:
    case ENROLLMENT_STATUS.REJECTED_BY_CENTRAL_MANAGER:
      return "reddedildi";
    case ENROLLMENT_STATUS.CANCELLED:
      return "iptal_edildi";
    case ENROLLMENT_STATUS.REQUESTED:
      return "ilk_onay_bekliyor";
    default:
      return "ilk_onay_bekliyor";
  }
}

/** UI status -> backend status listesi (filtre için) */
function personnelStatusToApiStatuses(
  status: PersonnelRequestEntry["status"],
): number[] {
  switch (status) {
    case "onaylandi":
      return [ENROLLMENT_STATUS.ACTIVE];
    case "tamamlandi":
      return [ENROLLMENT_STATUS.COMPLETED];
    case "ikinci_onay_bekliyor":
      return [ENROLLMENT_STATUS.APPROVED_EDUCATION_SUPERVISOR];
    case "reddedildi":
      return [
        ENROLLMENT_STATUS.REJECTED_BY_BRANCH_MANAGER,
        ENROLLMENT_STATUS.REJECTED_BY_CENTRAL_MANAGER,
      ];
    case "iptal_edildi":
      return [ENROLLMENT_STATUS.CANCELLED];
    case "ilk_onay_bekliyor":
      return [ENROLLMENT_STATUS.REQUESTED];
    case "ucuncu_onay_bekliyor":
      return [ENROLLMENT_STATUS.APPROVED_BRANCH_MANAGER];
    default:
      return [ENROLLMENT_STATUS.REQUESTED];
  }
}

function mapEnrollmentToPersonnelEntry(
  item: EducationEnrollmentItemApi,
): PersonnelRequestEntry {
  const u = item.user;
  const personelAdi =
    (u && [u.firstName, u.lastName].filter(Boolean).join(" ")) || "-";
  const tcKimlikNo = u?.identityNumber ?? "";
  const unvan = u?.title ?? "";
  const birim = u?.department?.name;
  return {
    id: item.id,
    userId: item.userID,
    personelAdi,
    tcKimlikNo,
    unvan,
    ...(birim ? { birim } : {}),
    status: apiEnrollmentStatusToPersonnelStatus(item.status),
    isBanned: item.isBanned ?? false,
    approval: item.approval ?? null,
  };
}

export const requestAndApprovalApi = {
  /**
   * Eğitim listesini backend'den alır (GET /api/admin/educations).
   * Filtreler: search, format (Yüz Yüze/Canlı), status (manage_requests / sent_for_approval / approved), type, categoryIds, instructorIds.
   */
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    sortBy?: EducationListParams["sortBy"];
    sortOrder?: EducationListParams["sortOrder"];
    filters?: RequestAndApprovalFilters;
  }): Promise<{ data: RequestAndApprovalEntry[]; total: number }> => {
    const page = params?.page ?? 1;
    const limit = params?.pageSize ?? 10;
    const filters = params?.filters ?? {};

    const apiParams: EducationListParams = {
      page,
      limit,
      search: filters.search || undefined,
      sortBy: params?.sortBy ?? "createdAt",
      sortOrder: params?.sortOrder ?? "desc",
    };

    if (filters.type !== undefined) {
      const val = Array.isArray(filters.type) ? filters.type[0] : filters.type;
      const num =
        typeof val === "number" && !Number.isNaN(val)
          ? val
          : parseInt(String(val), 10);
      if (!Number.isNaN(num)) apiParams.type = num;
    } else if (filters.format !== undefined) {
      const formats = Array.isArray(filters.format)
        ? filters.format
        : [filters.format];
      const first = formats[0];
      if (first) {
        const type = requestApprovalFormatToApiType(first);
        if (type !== undefined) apiParams.type = type;
      }
    }

    if (
      filters.status !== undefined &&
      (filters.status as unknown) !== "" &&
      !(Array.isArray(filters.status) && filters.status.length === 0)
    ) {
      const statuses = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
      const apiStatuses = statuses.flatMap(requestApprovalStatusToApiStatuses);
      if (apiStatuses.length > 0) {
        // Her durumda `statusIn` (array) ile gönderiyoruz — axios'un
        // paramsSerializer'ı tek `status=0` parametresini edge-case olarak
        // düşürebiliyor; ayrıca "Onaylandı" gibi 3 değere mapping yapan
        // filtrelerde de tek kod yolu kullanıyoruz.
        apiParams.statusIn = apiStatuses;
      }
    }

    if (filters.categoryIds?.length)
      apiParams.categoryIds = filters.categoryIds;
    if (filters.instructorIds?.length)
      apiParams.instructorIds = filters.instructorIds;

    const { data: response } = await apiClient.get<EducationListResponseApi>(
      ADMIN_EDUCATIONS_ENDPOINT,
      { params: apiParams },
    );

    const items = Array.isArray(response?.data) ? response.data : [];
    const total = response?.count ?? 0;

    return {
      data: items.map(mapApiItemToRequestAndApprovalEntry),
      total,
    };
  },

  /**
   * Eğitim sorumlusu ekranı: eğitime ait kayıtları listeler (ekibim).
   * GET /api/admin/educations/:educationId/enrollments/my-team
   */
  getPersonnelByEducationId: async (
    educationId: string,
    params?: {
      search?: string;
      status?:
        | PersonnelRequestEntry["status"]
        | PersonnelRequestEntry["status"][];
      page?: number;
      pageSize?: number;
    },
  ): Promise<{ data: PersonnelRequestEntry[]; total: number }> => {
    const page = params?.page ?? 1;
    const limit = params?.pageSize ?? 10;
    const queryParams: Record<string, unknown> = { page, limit };
    if (params?.search) queryParams.search = params.search;
    if (
      params?.status !== undefined &&
      (params.status as unknown) !== "" &&
      !(Array.isArray(params.status) && params.status.length === 0)
    ) {
      const statuses = Array.isArray(params.status)
        ? params.status
        : [params.status];
      // Boş string / geçersiz değerleri at — aksi halde default branch
      // tetiklenir ve istemeden REQUESTED filtresi uygular.
      const cleaned = statuses.filter(
        (s): s is PersonnelRequestEntry["status"] =>
          typeof s === "string" && s.length > 0,
      );
      const apiStatuses = cleaned.flatMap(personnelStatusToApiStatuses);
      // Birden fazla mapping olabilen ("reddedildi" -> [3,4]) durumlar için
      // backend tek integer beklediği için `statusIn` (array) ile gönderiyoruz.
      if (apiStatuses.length > 0) queryParams.statusIn = apiStatuses;
    }

    const { data: response } =
      await apiClient.get<EducationEnrollmentsResponseApi>(
        `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/my-team`,
        { params: queryParams },
      );

    const rawItems = response?.data ?? [];
    const items = Array.isArray(rawItems) ? rawItems : [];
    const total = response?.count ?? response?.total ?? items.length;

    const data = items.map(mapEnrollmentToPersonnelEntry);
    return { data, total: typeof total === "number" ? total : data.length };
  },

  /**
   * Toplu kayıt onayı / reddi.
   * PATCH /api/admin/educations/education-enrollments/bulk-review
   * Body: { enrollmentIds: string[], isApproved: boolean }
   */
  bulkReviewEnrollments: async (body: {
    enrollmentIds: string[];
    isApproved: boolean;
  }): Promise<void> => {
    await apiClient.patch(
      "/api/admin/educations/education-enrollments/bulk-review",
      body,
    );
  },

  /**
   * Eğitim yöneticisi: enrollment kaydını kalıcı siler.
   * DELETE /api/admin/educations/:educationId/enrollments/:enrollmentId
   */
  deleteEnrollment: async (
    educationId: string,
    enrollmentId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/${enrollmentId}`,
    );
  },

  // NOT: Eğitim tamamlama artık kullanıcı başına karar gerektiriyor; popover'daki
  // "Tamamla" aksiyonu `educationManagementApi.getCompleteCandidates` +
  // `educationManagementApi.completeEducation(body)` akışına (CompleteEducationModal) delege edilir.

  /**
   * Eğitim Yöneticisi accordion: birimlere göre enrollment'lar.
   * GET /api/admin/educations/:educationId/enrollments/by-department
   */
  getDepartmentsByEducationId: async (
    educationId: string,
    params?: {
      status?:
        | PersonnelRequestEntry["status"]
        | PersonnelRequestEntry["status"][];
      search?: string;
      activeOfferingOnly?: boolean;
    },
  ): Promise<{ data: DepartmentWithPersonnel[] }> => {
    const activeOfferingOnly = params?.activeOfferingOnly ?? true;
    const queryParams: Record<string, unknown> = {
      activeOfferingOnly: activeOfferingOnly ? "true" : "false",
    };

    if (
      params?.status !== undefined &&
      (params.status as unknown) !== "" &&
      !(Array.isArray(params.status) && params.status.length === 0)
    ) {
      const statuses = Array.isArray(params.status)
        ? params.status
        : [params.status];
      // Boş string / geçersiz değerleri at — aksi halde default branch
      // tetiklenir ve istemeden REQUESTED filtresi uygular.
      const cleaned = statuses.filter(
        (s): s is PersonnelRequestEntry["status"] =>
          typeof s === "string" && s.length > 0,
      );
      const apiStatuses = cleaned.flatMap(personnelStatusToApiStatuses);
      // Birden fazla mapping olabilen ("reddedildi" -> [3,4]) durumlar için
      // backend tek integer beklediği için `statusIn` (array) ile gönderiyoruz.
      if (apiStatuses.length > 0) queryParams.statusIn = apiStatuses;
    }
    if (params?.search) queryParams.search = params.search;

    const { data: response } = await apiClient.get<{
      data?: Array<{
        departmentId: string | null;
        departmentName: string | null;
        totalEnrollments: number;
        users: EducationEnrollmentItemApi[];
      }>;
      count?: number;
    }>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/by-department`,
      {
        params: queryParams,
      },
    );

    const items = response?.data ?? [];

    const data: DepartmentWithPersonnel[] = items.map((item) => {
      const personnel = (item.users ?? []).map(mapEnrollmentToPersonnelEntry);
      const onayCount = personnel.filter(
        (p) => p.status === "onaylandi" || p.status === "tamamlandi",
      ).length;
      return {
        id: item.departmentId ?? "no-department",
        name: item.departmentName ?? i18n.t("requestAndApproval.noDepartment"),
        talepCount: item.totalEnrollments ?? personnel.length,
        onayCount,
        personnel,
      };
    });

    return { data };
  },

  /**
   * Sınıf oluşturma akışı: kök Başkanlık / İşletme bazında enrollment'lar.
   * `getDepartmentsByEducationId` ile aynı response shape'i — sadece gruplama
   * anahtarı yaprak yerine kök ata.
   * GET /api/admin/educations/:educationId/enrollments/by-root-department
   */
  getRootDepartmentsByEducationId: async (
    educationId: string,
    params?: {
      status?:
        | PersonnelRequestEntry["status"]
        | PersonnelRequestEntry["status"][];
      search?: string;
      activeOfferingOnly?: boolean;
    },
  ): Promise<{ data: DepartmentWithPersonnel[] }> => {
    const activeOfferingOnly = params?.activeOfferingOnly ?? true;
    const queryParams: Record<string, unknown> = {
      activeOfferingOnly: activeOfferingOnly ? "true" : "false",
    };

    if (
      params?.status !== undefined &&
      (params.status as unknown) !== "" &&
      !(Array.isArray(params.status) && params.status.length === 0)
    ) {
      const statuses = Array.isArray(params.status)
        ? params.status
        : [params.status];
      const cleaned = statuses.filter(
        (s): s is PersonnelRequestEntry["status"] =>
          typeof s === "string" && s.length > 0,
      );
      const apiStatuses = cleaned.flatMap(personnelStatusToApiStatuses);
      if (apiStatuses.length > 0) queryParams.statusIn = apiStatuses;
    }
    if (params?.search) queryParams.search = params.search;

    type RootDeptItem = {
      departmentId: string | null;
      departmentName: string | null;
      totalEnrollments: number;
      users: EducationEnrollmentItemApi[];
    };
    const { data: response } = await apiClient.get<{
      data?: RootDeptItem[];
      count?: number;
    }>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/by-root-department`,
      {
        params: queryParams,
      },
    );

    const items: RootDeptItem[] = response?.data ?? [];

    const data: DepartmentWithPersonnel[] = items.map((item) => {
      const personnel = (item.users ?? []).map(mapEnrollmentToPersonnelEntry);
      const onayCount = personnel.filter(
        (p: PersonnelRequestEntry) =>
          p.status === "onaylandi" || p.status === "tamamlandi",
      ).length;
      return {
        id: item.departmentId ?? "no-department",
        name: item.departmentName ?? i18n.t("requestAndApproval.noDepartment"),
        talepCount: item.totalEnrollments ?? personnel.length,
        onayCount,
        personnel,
      };
    });

    return { data };
  },

  /**
   * Kayıtlı planlama sınıfları (salt okunur önizleme)
   * GET /api/admin/educations/:educationId/enrollments/planning-groups
   */
  getPlanningGroups: async (
    educationId: string,
  ): Promise<PlanningGroupsResponseApi> => {
    const { data: response } = await apiClient.get<
      PlanningGroupsResponseApi & { data?: PlanningGroupsResponseApi }
    >(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/planning-groups`,
    );
    const r = response as PlanningGroupsResponseApi & {
      data?: PlanningGroupsResponseApi;
    };
    const rawGroups = r?.groups ?? r?.data?.groups ?? [];
    const groups = rawGroups.map(normalizePlanningGroupFromApi);
    return { groups };
  },

  /**
   * Sınıf grupları ile onay (eğitim yöneticisi planlaması)
   * POST /api/admin/educations/:educationId/enrollments/approve-with-class-groups
   */
  approveWithClassGroups: async (
    educationId: string,
    body: ApproveWithClassGroupsPayload,
  ): Promise<ApproveWithClassGroupsResult> => {
    // Oluşan sınıfların ID'si de dönüyor: "kaydedince mail gönder" akışı Eğitim
    // Sorumlularını çözerken sınıf ID'sine ihtiyaç duyuyor.
    const { data: response } = await apiClient.post<
      ApproveWithClassGroupsResult & { data?: ApproveWithClassGroupsResult }
    >(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments/approve-with-class-groups`,
      body,
    );
    const classrooms = response?.classrooms ?? response?.data?.classrooms ?? [];
    return { classrooms: Array.isArray(classrooms) ? classrooms : [] };
  },

  /**
   * Final planlamayı yayınla — eğitimi ACTIVE durumuna geçirir ve talep toplamayı kapatır.
   * PATCH /api/admin/educations/:educationId
   */
  finalizePlanning: async (educationId: string): Promise<void> => {
    await apiClient.patch(`${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}`, {
      status: EDUCATION_STATUS_API.ACTIVE,
      isAcceptingRequestCollection: false,
    });
  },
};
